/**
 * 이미지 압축 유틸리티
 *
 * 오프라인 동기화 시 413 에러를 방지하기 위한 이미지 압축 기능
 * - 대용량 이미지 자동 압축
 * - 이미지 품질 조정
 * - EXIF 회전 정보 보정
 */

const DEFAULT_MAX_SIZE_MB = 1.5; // 최대 파일 크기 (MB) - 1.5MB 이상 압축 (더 공격적으로 압축)
const DEFAULT_QUALITY = 0.80; // 압축 품질 (0.0 ~ 1.0) - 품질 낮춰서 더 작게
const DEFAULT_MAX_DIMENSION = 1920; // 최대 너비/높이 (픽셀) - Full HD까지만

/**
 * 이미지 압축 설정
 */
const COMPRESSION_CONFIG = {
  // 파일 크기 제한 (바이트)
  maxSizeBytes: DEFAULT_MAX_SIZE_MB * 1024 * 1024,

  // 압축 품질
  quality: DEFAULT_QUALITY,

  // 최대 이미지 크기
  maxWidth: DEFAULT_MAX_DIMENSION,
  maxHeight: DEFAULT_MAX_DIMENSION,

  // 압축 대상 MIME 타입
  compressibleTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],

  // 압축 후 출력 형식
  outputFormat: 'image/jpeg', // JPEG가 가장 효율적
};

/**
 * 파일이 압축 가능한지 확인
 * @param {File} file - 확인할 파일 객체
 * @returns {boolean} 압축 가능 여부
 */
export function isCompressible(file) {
  if (!file || !file.type) return false;
  return COMPRESSION_CONFIG.compressibleTypes.includes(file.type.toLowerCase());
}

/**
 * 파일이 압축이 필요한지 확인 (크기 기준)
 * @param {File} file - 확인할 파일 객체
 * @returns {boolean} 압축 필요 여부
 */
export function needsCompression(file) {
  if (!file) return false;
  return file.size > COMPRESSION_CONFIG.maxSizeBytes;
}

/**
 * 이미지 회전 정보(EXIF Orientation) 보정
 * @param {HTMLImageElement} img - 이미지 엘리먼트
 * @param {CanvasRenderingContext2D} ctx - 캔버스 컨텍스트
 * @param {number} orientation - EXIF orientation 값
 */
function applyOrientation(img, ctx, orientation) {
  const { width, height } = img;

  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, height, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, width);
      break;
    default:
      break;
  }
}

/**
 * 이미지 크기 계산 (비율 유지하면서 최대 크기 제한)
 * @param {number} width - 원본 너비
 * @param {number} height - 원본 높이
 * @param {number} maxWidth - 최대 너비
 * @param {number} maxHeight - 최대 높이
 * @returns {{width: number, height: number}} 조정된 크기
 */
function calculateDimensions(width, height, maxWidth, maxHeight) {
  let newWidth = width;
  let newHeight = height;

  // 가로가 더 큰 경우
  if (width > height) {
    if (width > maxWidth) {
      newWidth = maxWidth;
      newHeight = Math.round((height * maxWidth) / width);
    }
  }
  // 세로가 더 큰 경우
  else {
    if (height > maxHeight) {
      newHeight = maxHeight;
      newWidth = Math.round((width * maxHeight) / height);
    }
  }

  return { width: newWidth, height: newHeight };
}

/**
 * 이미지 파일 압축
 * @param {File} file - 압축할 이미지 파일
 * @param {Object} options - 압축 옵션
 * @param {number} options.quality - 압축 품질 (0.0 ~ 1.0)
 * @param {number} options.maxWidth - 최대 너비
 * @param {number} options.maxHeight - 최대 높이
 * @param {string} options.outputFormat - 출력 형식
 * @returns {Promise<File>} 압축된 이미지 파일
 */
export async function compressImage(file, options = {}) {
  // 압축 옵션 병합
  const config = {
    quality: options.quality || COMPRESSION_CONFIG.quality,
    maxWidth: options.maxWidth || COMPRESSION_CONFIG.maxWidth,
    maxHeight: options.maxHeight || COMPRESSION_CONFIG.maxHeight,
    outputFormat: options.outputFormat || COMPRESSION_CONFIG.outputFormat,
  };

  return new Promise((resolve, reject) => {
    // 이미지가 아니면 원본 반환
    if (!isCompressible(file)) {
      // console.log(`[ImageCompression] 압축 불가능한 파일 타입: ${file.type}`);
      resolve(file);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          // 이미지 크기 계산
          const dimensions = calculateDimensions(
            img.width,
            img.height,
            config.maxWidth,
            config.maxHeight
          );

          // 캔버스 생성
          const canvas = document.createElement('canvas');
          canvas.width = dimensions.width;
          canvas.height = dimensions.height;

          const ctx = canvas.getContext('2d');

          // 이미지 그리기 (고품질 렌더링)
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);

          // Blob으로 변환
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('이미지 압축 실패: Blob 생성 실패'));
                return;
              }

              // File 객체 생성
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '.jpg'), // 확장자를 .jpg로 변경
                {
                  type: config.outputFormat,
                  lastModified: Date.now(),
                }
              );

              // 압축 결과 로그
              const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
              const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
              const reductionPercent = (
                ((file.size - compressedFile.size) / file.size) * 100
              ).toFixed(1);

              // console.log(
              //   `[ImageCompression] 압축 완료: ${file.name}\n` +
              //   `  원본: ${originalSizeMB}MB (${img.width}x${img.height})\n` +
              //   `  압축: ${compressedSizeMB}MB (${dimensions.width}x${dimensions.height})\n` +
              //   `  감소율: ${reductionPercent}%`
              // );

              resolve(compressedFile);
            },
            config.outputFormat,
            config.quality
          );
        } catch (error) {
          // console.error('[ImageCompression] 압축 중 에러:', error);
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('이미지 로드 실패'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('파일 읽기 실패'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * 여러 이미지 파일 일괄 압축
 * @param {File[]} files - 압축할 이미지 파일 배열
 * @param {Object} options - 압축 옵션
 * @param {Function} onProgress - 진행률 콜백 (current, total)
 * @returns {Promise<File[]>} 압축된 이미지 파일 배열
 */
export async function compressImages(files, options = {}, onProgress = null) {
  if (!Array.isArray(files) || files.length === 0) {
    return [];
  }

  const results = [];
  let processed = 0;

  // console.log(`[ImageCompression] ${files.length}개 이미지 일괄 압축 시작`);

  for (const file of files) {
    try {
      // 압축 필요 여부 확인
      if (isCompressible(file) && needsCompression(file)) {
        const compressed = await compressImage(file, options);
        results.push(compressed);
      } else {
        // 압축 불필요하면 원본 사용
        // console.log(`[ImageCompression] 압축 건너뜀: ${file.name} (${(file.size / 1024).toFixed(0)}KB)`);
        results.push(file);
      }
    } catch (error) {
      // console.error(`[ImageCompression] 압축 실패: ${file.name}`, error);
      // 에러 발생시 원본 사용
      results.push(file);
    }

    processed++;

    // 진행률 콜백
    if (onProgress) {
      onProgress(processed, files.length);
    }
  }

  // console.log(`[ImageCompression] 일괄 압축 완료: ${files.length}개`);

  return results;
}

/**
 * 압축 필요 여부 확인 및 자동 압축
 * @param {File} file - 확인/압축할 파일
 * @param {Object} options - 압축 옵션
 * @returns {Promise<File>} 압축된 파일 또는 원본 파일
 */
export async function autoCompressIfNeeded(file, options = {}) {
  if (!file) return file;

  // 압축 가능하고 필요한 경우만 압축
  if (isCompressible(file) && needsCompression(file)) {
    try {
      return await compressImage(file, options);
    } catch (error) {
      // console.error('[ImageCompression] 자동 압축 실패, 원본 사용:', error);
      return file;
    }
  }

  return file;
}

export default {
  compressImage,
  compressImages,
  autoCompressIfNeeded,
  isCompressible,
  needsCompression,
  COMPRESSION_CONFIG,
};
